import moment from "moment"
import _cloneDeep from "lodash/cloneDeep";
import _isEmpty from "lodash/isEmpty";

const state = {
    followshipBooks: [],
    bookInsights: [],
    bookInsightPreviews: [],
    isUpdatingLibrary: false
}

const mutations = {
    CLEAN_UP_DATA(state) {
        state.followshipBooks = [];
        state.bookInsights = [];
        state.bookInsightPreviews = [];
    },
    SET_FOLLOWSHIP_BOOKS(state, followshipBooks = []) {
        state.followshipBooks = followshipBooks;
    },
    SET_USER_BOOKS(state, userAudiobooks = []) {
        state.userAudiobooks = userAudiobooks;
    },
    SET_USER_BOOK_INSIGHTS(state, bookInsights = []) {
        state.bookInsights = bookInsights;
    },
    SET_USER_BOOK_INSIGHT_PREVIEWS(state, bookInsightPreviews = []) {
        state.bookInsightPreviews = bookInsightPreviews;
    },
    SET_UPDATING_LIBRARY(state, isUpdating) {
        state.isUpdatingLibrary = isUpdating;
    }
}

const actions = {
    getData({ dispatch }) {
        // get the user books
        dispatch("updateLibraryBooks");
        dispatch("updateUserBooks");
        dispatch("updateUserBookInsights");
    },
    setFollowshipBooks({ commit }, followshipBooks = []) {
        commit("SET_FOLLOWSHIP_BOOKS", followshipBooks);
    },
    setUserBooks({ commit }, userBooks = []) {
        commit("SET_USER_BOOKS", userBooks);
    },
    setUserBookInsights({ commit }, bookInsights = []) {
        commit("SET_USER_BOOK_INSIGHTS", bookInsights);
    },
    getFollowshipBooks() {
        return axios.get("v2/followship/audiobooks?sort=alphabetic");
    },
    getUserBooks() {
        return axios.get("v2/user/audiobooks");
    },
    getUserBookInsights() {
        return axios.get("v2/user/book_insights");
    },
    getUserBookInsightPreviews({}, bookInsights) {
        const bookInsightIds = bookInsights.map((bookInsight) => {
            return bookInsight.book_insight_id;
        });
        return axiosCms.get(`/site/book-insight-previews?q=(id:${bookInsightIds.join("|")})`);
    },
    cleanUpData({ commit }) {
        commit("CLEAN_UP_DATA");
    },
    updateLibraryBooks({ dispatch }) {
        return new Promise((resolve) => {
            dispatch("getFollowshipBooks").then(({ data }) => {
                dispatch("setFollowshipBooks", data);
            }).finally(() => resolve());
        });
    },
    updateUserBooks({ dispatch }) {
        return new Promise((resolve) => {
            dispatch("getUserBooks").then(({ data }) => {
                if (Array.isArray(data)) {
                    dispatch("setUserBooks", data);
                }
            }).finally(() => resolve());
        });
    },
    updateBooksInfo({ dispatch }) {
        return new Promise((resolve) => {
            Promise.all([
                dispatch("updateUserBooks"),
                dispatch("updateLibraryBooks"),
                dispatch("UserWallet/updateCoins", null, { root: true })
            ]).finally(() => resolve());
        });
    },
    updateUserBookInsights({ commit, dispatch }) {
        commit("SET_UPDATING_LIBRARY", true);
        return new Promise((resolve) => {
            dispatch("getUserBookInsights").then(({ data: bookInsights }) => {
                dispatch("setUserBookInsights", bookInsights);
                dispatch("updateUserBookInsightPreviews", bookInsights);
            }).finally(() => {
                commit("SET_UPDATING_LIBRARY", false);
                resolve();
            });
        });
    },
    updateUserBookInsightPreviews({ dispatch, commit }, bookInsightIds) {
        return new Promise((resolve) => {
            if (!bookInsightIds.length) {
                return commit("SET_USER_BOOK_INSIGHT_PREVIEWS", []);
            }

            dispatch("getUserBookInsightPreviews", bookInsightIds).then(({ data: bookInsightPreviews }) => {
                commit("SET_USER_BOOK_INSIGHT_PREVIEWS", bookInsightPreviews);
            }).finally(() => {
                resolve();
            });
        });
    }
}

const getters = {
    userBookInsightIds(state) {
        return state.bookInsights.map((bookInsight) => {
            return bookInsight.book_insight_id;
        });
    },
    bookInsightsByAuthor(state) {
        const clonedPreviews = _cloneDeep(state.bookInsightPreviews);
        return clonedPreviews.sort((leftBookInsight, rightBookInsight) => {
            const leftAuthor = leftBookInsight.authors[0].name;
            const rightAuthor = rightBookInsight.authors[0].name;
            if (leftAuthor < rightAuthor) {
                return -1;
            } else if (leftAuthor > rightAuthor) {
                return 1;
            }
            return 0;
        });
    },
    bookInsightsByTitle(state) {
        const clonedPreviews = _cloneDeep(state.bookInsightPreviews);
        return clonedPreviews.sort((leftBookInsight, rightBookInsight) => {
            if (leftBookInsight.title < rightBookInsight.title) {
                return -1;
            } else if (leftBookInsight.title > rightBookInsight.title) {
                return 1;
            }
            return 0;
        });
    },
    bookInsightsByCreatedDate(state) {
        const sortedBookInsightPreviews = [];

        const clonedBookInsights = _cloneDeep(state.bookInsights);
        const orderedSourceArray = clonedBookInsights.sort((leftBi, rightBi) => {
            const leftBiDate = moment(leftBi.created_at);
            const rightBiDate = moment(rightBi.created_at);
            if (leftBiDate < rightBiDate) {
                return 1;
            } else if (leftBiDate > rightBiDate) {
                return -1;
            }
            return 0;
        }).map((item) => {
            return item.book_insight_id;
        })

        orderedSourceArray.forEach((bookInsightId) => {
            const bookInsightPreview = state.bookInsightPreviews.find((bookInsight) => {
                return bookInsight.id === bookInsightId;
            })
            if (!_isEmpty(bookInsightPreview)) {
                sortedBookInsightPreviews.push(bookInsightPreview);
            }
        })
        return sortedBookInsightPreviews;
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
